import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Button,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    LinearProgress,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    Download as DownloadIcon,
    Receipt as ReceiptIcon,
    CreditCard as CardIcon,
    Warning as WarningIcon,
    Cancel as CancelIcon,
    History as HistoryIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { getSubDetails } from '../../hooks/apiHandler';
import { ALL_COUNTRIES, ALL_COUNTRIES_SERVICE_PROVIDER } from '../../helperFunc/countylist';
import { planCards, storeCards } from '../../helperFunc/planList';

/**
 * BILLING & SUBSCRIPTION PAGE
 * Integrated for bitdeploy inc. (SZG104000)
 */
export default function BillingPage({ storeInfo }) {
    // --- STATE ---
    const [loading, setLoading] = useState(true);
    const sTy = storeCards.find((e) => e.id === storeInfo.storeType);
    const pTy = planCards.find((e) => e.id === storeInfo.planType);
    const pname = sTy.title + "-" + pTy.title;
    const [subscription, setSubscription] = useState({
        planName: pname,
        status: "inactive",
        price: 0,
        interval: "month",
        expiryDate: "",
        autoRenew: true,
        storeID: storeInfo.storeID
    });

    const [invoices, setInvoices] = useState([]);
    const [openCancel, setOpenCancel] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const regionInfo = ALL_COUNTRIES.find((e) => e.code === storeInfo.country);
    const serviceProvide = ALL_COUNTRIES_SERVICE_PROVIDER.find((e) => e.country === storeInfo.country);


    // --- DATE FORMATTING ---
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const formatPeriod = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };

    // --- DATA FETCHING ---
    const fetchStoreData = async () => {
        try {
            setLoading(true);
            const storeidLocal = JSON.parse(window.localStorage.getItem('ds')) || storeInfo.storeID;

            // Using the mock function to simulate apiHandler behavior
            const response = await getSubDetails({ storeID: storeidLocal });

            if (response.code === 0 && response.data) {
                const { subStatus, subHistry } = response.data;

                setSubscription({
                    planName: pname,
                    status: subStatus.status,
                    price: subHistry.length > 0 ? subHistry[0].amountPaid / 100 : 0,
                    interval: "month",
                    expiryDate: subStatus.currentPeriodEnd,
                    autoRenew: !subStatus.cancelAtPeriodEnd,
                    storeID: subStatus.storeID
                });

                const mappedInvoices = subHistry.map(inv => ({
                    id: inv.stripeInvoiceID,
                    shortId: inv.stripeInvoiceID.substring(inv.stripeInvoiceID.length - 8).toUpperCase(),
                    date: formatDate(inv.paidAt),
                    dueDate: formatDate(inv.paidAt),
                    amount: inv.amountPaid / 100,
                    status: inv.status.toUpperCase(),
                    periodStart: formatPeriod(subStatus.currentPeriodStart),
                    periodEnd: formatDate(subStatus.currentPeriodEnd),
                }));

                setInvoices(mappedInvoices);
            }
        } catch (error) {
            console.error("Error fetching store data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStoreData();
    }, []);

    useEffect(() => {
        if (selectedInvoice) {
            const timer = setTimeout(() => {
                window.print();
                setSelectedInvoice(null);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [selectedInvoice]);

    const daysLeft = subscription.expiryDate
        ? Math.ceil((new Date(subscription.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))
        : 0;
    const progress = Math.max(0, Math.min(100, 100 - ((daysLeft / 30) * 100)));

    const handleCancelClick = () => setOpenCancel(true);
    const handleCloseCancel = () => setOpenCancel(false);

    const confirmCancellation = () => {
        setSubscription(prev => ({ ...prev, status: 'cancelled', autoRenew: false }));
        setOpenCancel(false);
    };

    const handleDownloadInvoice = (invoice) => {
        setSelectedInvoice(invoice);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <style>
                {`
          @media print {
            @page { margin: 0; size: auto; }
            body { margin: 1.9cm; background: white !important; -webkit-print-color-adjust: exact; }
            body * { visibility: hidden; }
            .printable-invoice, .printable-invoice * { visibility: visible; }
            .printable-invoice { position: absolute; left: 0; top: 0; width: 100% !important; display: block !important; box-shadow: none !important; }
            .no-print { display: none !important; }
          }
        `}
            </style>

            {/* Main UI Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }} className="no-print">
                <Box>
                    <Typography variant="h4" fontWeight="900" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                        Billing & Subscription
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Merchant: <strong>{storeInfo.name}</strong> | ID: {subscription.storeID}
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={3} className="no-print">
                {/* Active Plan Card */}
                <Grid item xs={12} md={5}>
                    <Card variant="outlined" sx={{ borderRadius: 4, height: '100%', position: 'relative', overflow: 'visible' }}>
                        <Box sx={{
                            position: 'absolute', top: -12, right: 20, zIndex: 1,
                            bgcolor: subscription.status === 'active' ? 'success.main' : 'error.main',
                            color: 'white', px: 2, py: 0.5, borderRadius: 1, boxShadow: 2,
                            fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem'
                        }}>
                            {subscription.status}
                        </Box>
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                            <Typography variant="overline" color="text.secondary" fontWeight="bold">Current Plan</Typography>
                            <Typography variant="h5" fontWeight="bold">{subscription.planName}</Typography>

                            <Box sx={{ my: 3 }}>
                                <Typography variant="h3" fontWeight="900" sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
                                    {regionInfo.symbol}{subscription.price.toFixed(2)}
                                    <Typography component="span" variant="h6" color="text.secondary">/{subscription.interval}</Typography>
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            <Stack spacing={2}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">Next Renewal:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{formatDate(subscription.expiryDate)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">Auto-Renew:</Typography>
                                    <Chip
                                        size="small"
                                        label={subscription.autoRenew ? "ON" : "OFF"}
                                        color={subscription.autoRenew ? "success" : "default"}
                                        variant="outlined"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                </Box>
                            </Stack>

                            <Box sx={{ mt: 4 }}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="caption" fontWeight="bold">Term Progress</Typography>
                                    <Typography variant="caption">{daysLeft} Days Remaining</Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 5 }} />
                            </Box>

                            {/* <Box sx={{ mt: 4 }}>
                                {subscription.status === 'active' ? (
                                    <Button fullWidth variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancelClick} sx={{ py: 1.2 }}>
                                        Cancel Subscription
                                    </Button>
                                ) : (
                                    <Button fullWidth variant="contained" color="primary" sx={{ py: 1.2 }}>
                                        Reactivate Plan
                                    </Button>
                                )}
                            </Box> */}
                        </CardContent>
                    </Card>
                </Grid>

                {/* History Table */}
                <Grid item xs={12} md={7}>
                    <Paper variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
                        <Box sx={{ p: 3, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HistoryIcon color="primary" />
                            <Typography variant="h6" fontWeight="bold">Invoice History</Typography>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead sx={{ bgcolor: '#fafafa' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices.length > 0 ? invoices.map((inv) => (
                                        <TableRow key={inv.id} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Typography variant="body2" fontWeight="bold">{inv.date}</Typography>
                                                <Typography variant="caption" color="text.secondary">#{inv.shortId}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={inv.status}
                                                    size="small"
                                                    color={inv.status === 'PAID' ? 'success' : 'warning'}
                                                    variant="soft"
                                                    sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>{regionInfo.symbol}{inv.amount.toFixed(2)}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Tooltip title="Print Invoice">
                                                        <IconButton size="small" color="primary" onClick={() => handleDownloadInvoice(inv)}>
                                                            <PrintIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                                No billing history found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* HIDDEN PRINTABLE AREA */}
            {selectedInvoice && (
                <Box className="printable-invoice" sx={{ display: 'none', p: 6, bgcolor: 'white', color: 'black' }}>
                    {/* Company Logo / Brand */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 1 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 900,
                                letterSpacing: '-0.5px',
                                color: 'primary.main',
                                borderLeft: '5px solid',
                                pl: 1.5,
                                borderColor: 'primary.main'
                            }}
                        >
                            MiiRA POS
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 6, alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="h4" fontWeight="bold">INVOICE</Typography>
                            <Chip label={selectedInvoice.status} color="success" size="small" sx={{ fontWeight: 'bold' }} />
                        </Box>
                        <Box textAlign="right">
                            <Typography variant="h6" color="text.secondary">#{selectedInvoice.id}</Typography>
                            <Typography variant="body2">Paid on: {selectedInvoice.date}</Typography>
                        </Box>
                    </Box>

                    <Grid container spacing={4} sx={{ mb: 6 }}>
                        <Grid item xs={6}>
                            <Typography fontWeight="bold" variant="subtitle1" sx={{ color: 'primary.main', mb: 1 }}>Service Provider</Typography>
                            <Typography variant="body1" fontWeight="bold">{serviceProvide.name}</Typography>
                            <Typography variant="body2">{serviceProvide.address}</Typography>
                            <Typography variant="body2">{serviceProvide.city}, {serviceProvide.state} {serviceProvide.postCode}</Typography>
                            <Typography variant="body2">{serviceProvide.country}</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>{serviceProvide.email}</Typography>
                            {(serviceProvide.gst) ? (<Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>{regionInfo.taxLabel}: {serviceProvide.gst}</Typography>) : ""}
                        </Grid>
                        <Grid item xs={6} align="right">
                            <Typography fontWeight="bold" variant="subtitle1" sx={{ color: 'primary.main', mb: 1 }}>Bill To</Typography>
                            <Typography variant="body1" fontWeight="bold">{storeInfo.name}</Typography>
                            <Typography variant="body2">Store ID: {subscription.storeID}</Typography>
                            <Typography variant="body2">{storeInfo.address}</Typography>
                            <Typography variant="body2">{storeInfo.city}, {storeInfo.state} {storeInfo.postCode}</Typography>
                            <Typography variant="body2">{storeInfo.country}</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>{storeInfo.email}</Typography>
                            {(storeInfo.gst) ? (<Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>{regionInfo.taxLabel}: {storeInfo.gst}</Typography>) : ""}
                        </Grid>
                    </Grid>

                    <TableContainer sx={{ mb: 4 }}>
                        <Table sx={{ borderTop: '2px solid black' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>
                                        <Typography fontWeight="bold">{subscription.planName}</Typography>
                                        <Typography variant="caption" color="text.secondary">Subscription Period: {selectedInvoice.periodStart} - {selectedInvoice.periodEnd}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{selectedInvoice.amount.toFixed(2)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ ml: 'auto', width: 280 }}>
                        <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography>Subtotal:</Typography>
                                <Typography>{selectedInvoice.amount.toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography>Tax (0%):</Typography>
                                <Typography>0.00</Typography>
                            </Box>
                            <Divider sx={{ my: 1, borderColor: 'black', borderBottomWidth: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6" fontWeight="bold">Total Paid:</Typography>
                                <Typography variant="h6" fontWeight="bold">{regionInfo.symbol}{selectedInvoice.amount.toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'success.main' }}>
                                <Typography variant="caption" fontWeight="bold">Amount Due:</Typography>
                                <Typography variant="caption" fontWeight="bold">{regionInfo.symbol}0.00</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={{ mt: 10, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="caption">Thank you for partnering with miira corp. for your business POS solutions.</Typography>
                    </Box>
                </Box>
            )}

            {/* Cancel Dialog */}
            <Dialog open={openCancel} onClose={handleCloseCancel} className="no-print">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <WarningIcon /> Cancel Subscription?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your access will continue until <strong>{formatDate(subscription.expiryDate)}</strong>.
                        After this, your POS terminals and data exports will be restricted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseCancel}>Go Back</Button>
                    <Button onClick={confirmCancellation} variant="contained" color="error">Confirm Cancellation</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}